import { useRef, useState, type DragEvent } from "react";
import { Check, ImagePlus, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { ArtworkIssue } from "../../lib/metadata-builder/artwork";
import { uploadBuilderArtwork } from "../../lib/metadata-builder/upload-artwork";
import { builderSessionId, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

function issueMessage(issue: ArtworkIssue, t: TFunction) {
  switch (issue.code) {
    case "unsupported": return t("builder.artwork.unsupported", { file: issue.file });
    case "name": return t("builder.artwork.name", { file: issue.file });
    case "decode": return t("builder.artwork.decode", { file: issue.file });
    case "normalize": return t("builder.artwork.normalize", { file: issue.file });
    case "duplicate": return t("builder.artwork.duplicate", { file: issue.file, name: issue.name });
    case "missing": return t("builder.artwork.missing", { files: issue.names.join(", ") });
    case "dimensions": return t("builder.artwork.dimensions", { ...issue });
  }
}

export function ArtworkUpload() {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const uploading = useMetadataBuilderStore(state => state.uploading);
  const issues = useMetadataBuilderStore(state => state.uploadIssues);
  const artwork = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.artwork);
  const hasMargins = artwork && artwork.sourceWidth / artwork.sourceHeight !== 1920 / 1080;
  const rows = [
    { file: "base", count: "1", required: true, matched: !!artwork, purpose: t("builder.artwork.base") },
    { file: "a01–a07", count: "7", required: true, matched: !!artwork, purpose: t("builder.artwork.single") },
    { file: "b01–b07", count: "7", required: true, matched: !!artwork, purpose: t("builder.artwork.double") },
    { file: "c01–c07", count: "7", required: false, matched: !!artwork && artwork.missingOffline.length === 0, purpose: t("builder.artwork.offline") },
  ];
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void uploadBuilderArtwork(Array.from(event.dataTransfer.files));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {rows.map(row => <div key={row.file} className={cn("flex items-center gap-3 rounded border p-3", row.matched ? "border-green-200 bg-green-50 text-green-800" : "border-neutral-200 bg-white")}>
          <svg viewBox="0 0 64 48" className="size-12 shrink-0" aria-hidden="true">
            <rect x="5" y="5" width="42" height="32" rx="3" fill="currentColor" opacity=".12" />
            <rect x="11" y="9" width="42" height="32" rx="3" fill="currentColor" opacity=".2" />
            <rect x="17" y="13" width="42" height="32" rx="3" fill="white" stroke="currentColor" strokeWidth="2" />
            <circle cx="29" cy="23" r="3" fill="currentColor" opacity=".6" />
            <path d="m21 39 10-10 6 6 5-5 9 9" fill="currentColor" opacity=".4" />
            <text x="54" y="12" textAnchor="end" fontSize="10" fontWeight="700" fill="currentColor">×{row.count}</text>
          </svg>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium">
              {row.matched && <Check className="size-3.5 shrink-0" />}
              <span>{row.purpose}</span>
            </div>
            <p className="font-mono text-xs">{row.file}{row.count === "1" ? ".png / .webp" : ".png or .webp"}</p>
            <p className="text-[11px] text-neutral-500">{t(row.required ? "builder.artwork.required" : "builder.artwork.optional")}</p>
          </div>
        </div>)}
      </div>
      <div onDragOver={event => { event.preventDefault(); setDragging(true); }}
        onDragLeave={event => {
          const related = event.relatedTarget;
          if (!(related instanceof Node) || !event.currentTarget.contains(related)) setDragging(false);
        }}
        onDrop={onDrop}
        className={cn("space-y-2 rounded border-2 border-dashed p-4", dragging ? "border-neutral-900 bg-neutral-100" : "border-neutral-300")}>
        <p>{t("builder.artwork.selectHelp")}</p>
        <Button variant="outline" size="sm" disabled={uploading} onClick={() => picker.current?.click()}>
          {uploading ? <LoaderCircle className="animate-spin" /> : <ImagePlus />}
          {t(uploading ? "builder.artwork.processing" : artwork ? "builder.artwork.replace" : "builder.artwork.select")}
        </Button>
        <input ref={picker} type="file" multiple accept=".png,.webp,image/png,image/webp" className="hidden" onChange={event => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          void uploadBuilderArtwork(files);
        }} />
      </div>
      {issues.length > 0 && <div role="alert" className="space-y-2 rounded border border-red-200 bg-red-50 p-3 text-red-700">
        <p className="font-semibold">{t("builder.artwork.fixErrors")}</p>
        <ul className="list-disc space-y-1 break-words pl-4">{issues.map((issue, index) => <li key={index}>{issueMessage(issue, t)}</li>)}</ul>
        {artwork && <p>{t("builder.artwork.previousPreview")}</p>}
      </div>}
      {artwork && <div className="space-y-2 rounded border border-neutral-200 bg-neutral-50 p-3">
        <p>{t("builder.artwork.ready", { width: artwork.sourceWidth, height: artwork.sourceHeight })}</p>
        <p>{t(hasMargins ? "builder.artwork.fitMargins" : "builder.artwork.fit")}</p>
        {artwork.missingOffline.length > 0 && <p>{t("builder.artwork.fallback", {
          files: artwork.missingOffline.map(name => `${name} → a${name.slice(1)}`).join(", "),
        })}</p>}
      </div>}
    </div>
  );
}
